import { useState } from "react";
import { Form, Link, useRouteLoaderData, useSearchParams } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import type { LatLngTuple } from "leaflet";

import AutoComplete from "src/components/common/autoComplete";
import {
    handleFilesInput,
    projectCategoryColorCode,
    subPipelineOptions,
    timepicker,
} from "src/components/common/functions";
import type { SelectedTechnicians, TechOption, Work, WorkKeyword, WorkProject } from "./types";
import { defaultWork, laborCategorys } from "./types";
import { AsyncFormSelect, SelectOption } from "../common/AsyncSelect";

function toCoords(value: string | number[] | undefined): LatLngTuple | undefined {
    if (Array.isArray(value)) {
        if (value.length < 2) return undefined;
        const [lat, lng] = value;
        return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : undefined;
    }
    if (typeof value !== "string" || !value) return undefined;
    const [lat, lng] = value.split(",").map(Number);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : undefined;
}

export default function WorkForm({ keyword }: { keyword: WorkKeyword }) {
    const work = (useRouteLoaderData("work") as Work | undefined) ?? defaultWork;
    const [searchParams] = useSearchParams();
    const queryProjectId = searchParams.get("projectId") ?? "";
    const [formValues, setFormValues] = useState({
        projectId:            String(work.projectId ?? queryProjectId ?? ""),
        projectName:          work.projectName ?? "",
        serviceId:            String(work.serviceId ?? ""),
        serviceName:          work.serviceName ?? "",
        allDay:               work.allDay ?? "yes",
        startDate:            work.startTime ? work.startTime.slice(0, 10) : "",
        startTime:            work.startTime ? work.startTime.slice(11, 16) : "",
        endDate:              work.endTime ? work.endTime.slice(0, 10) : "",
        endTime:              work.endTime ? work.endTime.slice(11, 16) : "",
        location:             work.location ?? "",
        coords:               work.coords ?? "",
        category:             work.category ?? "",
        subCategory:          work.subCategory ?? "",
        supervisorId:         work.supervisorId ?? "",
        supervisorName:       work.supervisorName ?? "",
        fieldSupervisorName:  work.fieldSupervisorName ?? "",
        fieldSupervisorEmail: work.fieldSupervisorEmail ?? "",
        siteContactId:        String(work.siteContactId ?? ""),
        siteContactName:      work.siteContactName ?? "",
        cadRequired:          work.cadRequired ?? "no",
        reportRequired:       work.reportRequired ?? "no",
        waiveJSA:             work.waiveJSA ?? "no",
        jobTagLocation:       work.jobTagLocation ?? "no",
        description:          work.description ?? "",
        leadId:               work.leadId ?? "",
    
        projectLocation:      work.projectLocation ?? "",
        projectCoords:        work.projectCoords ?? "",
        organizationId:       work.organizationId ?? "",
    });

    const [selectedUsers, setSelectedUsers] = useState<SelectedTechnicians[]>(work.technicians ? (work.technicians as unknown as SelectedTechnicians[]) : []);

    const selectedTechOptions: TechOption[] = selectedUsers.map(user => ({
        value:         user.userId,
        label:         user.userName,
        
        id:            user.id,
        assignmentId:  user.assignmentId,
        userId:        user.userId,
        userName:      user.userName,
        laborCategory: user.laborCategory || "",
        fleetNumber:   user.fleetNumber || "",
        perDiem:       user.perDiem || "no",
    }));

    const subCategoryOptions: string[] = subPipelineOptions[formValues.category as keyof typeof subPipelineOptions] ?? [];
    const [project, setProject] = useState<WorkProject>({ organizationId: formValues.organizationId, location: formValues.projectLocation, coords: formValues.projectCoords });
    const usesTimedSchedule = formValues.allDay === "no";
    const hasScheduleBounds = Boolean(
        formValues.startDate &&
        formValues.endDate &&
        (!usesTimedSchedule || (formValues.startTime && formValues.endTime))
    );
    const technicianSelectEnabled = Boolean(formValues.serviceId && hasScheduleBounds);
    const normalizedStartTime = formValues.startDate
        ? `${formValues.startDate} ${usesTimedSchedule ? formValues.startTime : "00:00"}`
        : "";
    const normalizedEndTime = formValues.endDate
        ? `${formValues.endDate} ${usesTimedSchedule ? formValues.endTime : "23:59"}`
        : "";

    const handleTechniciansChange = (nextValue: SelectOption[] | null) => {
        const nextUsers: SelectedTechnicians[] = (nextValue ?? []).map(option => {
            const existing = selectedUsers.find(item => item.userId === option.value);
            return existing ?? {
                assignmentId: undefined,
                userId:       option.value,
                userName:     option.label,
                laborCategory: "",
                fleetNumber:   "",
                fleetName:     "",
                perDiem:       "no",
            };
        });
        setSelectedUsers(nextUsers);
        if (formValues.leadId && !nextUsers.find(item => item.userId === formValues.leadId)) {
            setFormValues({ ...formValues, leadId: "" });
        }
    };

    const updateSelectedUser = (id: string, key: keyof SelectedTechnicians, value: string) => {
        setSelectedUsers(items =>
            items.map(item => item.userId === id ? { ...item, [key]: value } : item)
        );
    };

    return (
        <Form
            method="post"
            onSubmit={event => {
                if (new Date(normalizedStartTime.replace(/-/g, "/")) > new Date(normalizedEndTime.replace(/-/g, "/"))) {
                    event.preventDefault();
                    window.alert("End time must be greater than or equal to start time.");
                    return;
                }
                if (selectedUsers.some(user => !user.laborCategory)) {
                    event.preventDefault();
                    window.alert("Please select a labor category for each technician.");
                    return;
                }
                if (selectedUsers.length && !formValues.leadId) {
                    event.preventDefault();
                    window.alert("Please select the JSA leadId.");
                }
            }}
        >
            {/* ── hidden fields ───────────────────────────────────────── */}
            <input type="hidden" name="action"               value={keyword === "Copy" ? "Add" : keyword} />
            <input type="hidden" name="id"                   value={work.id ?? ""} />
            <input type="hidden" name="allDay"               value={formValues.allDay} />
            <input type="hidden" name="startTime"            value={normalizedStartTime} />
            <input type="hidden" name="endTime"              value={normalizedEndTime} />
            <input type="hidden" name="serviceId"            value={formValues.serviceId} />
            <input type="hidden" name="cadRequired"          value={formValues.cadRequired} />
            <input type="hidden" name="reportRequired"       value={formValues.reportRequired} />
            <input type="hidden" name="waiveJSA"             value={formValues.waiveJSA} />
            <input type="hidden" name="jobTagLocation"       value={formValues.jobTagLocation} />
            <input type="hidden" name="leadId"               value={formValues.leadId} />
            <input type="hidden" name="description"          value={formValues.description} />
            <input
                type="hidden"
                name="technicians"
                value={JSON.stringify(
                    selectedUsers.map(user =>
                        JSON.stringify({
                            assignmentId:  user.assignmentId,
                            id:            user.id,
                            userId:        user.userId,
                            laborCategory: user.laborCategory,
                            fleetNumber:   user.fleetNumber,
                            perDiem:       user.perDiem
                        })
                    )
                )}
            />

            {/* ── visible fields ──────────────────────────────────────── */}
            <div className="cssSeperateWhenWide ps-3 mb-2">
                <div className="flex-grow-1 me-3">

                    {/* Project */}
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="projectId">
                            Project:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder="Search projects…"
                            required={true}
                            name="projectId"
                            inputId="projectId"
                            fileName="common/searchProject.php"
                            value={formValues.projectId ? { value: formValues.projectId, label: formValues.projectName } : null}
                            onChange={next => {
                                setFormValues({
                                    ...formValues, 
                                    projectId: (next as SelectOption)?.value ?? "", 
                                    projectName: (next as SelectOption)?.label ?? "",
                                    serviceId: "",
                                    serviceName: "",
                                    leadId: "",
                                });
                                setSelectedUsers([]);
                                setProject({
                                    organizationId: (next as (SelectOption & { organizationId: number }))?.organizationId ?? "",
                                    proposalId: (next as (SelectOption & { proposalId: number }))?.proposalId ?? "",
                                    location: (next as (SelectOption & { location: string }))?.location ?? "",
                                    coords: (next as (SelectOption & { coords: string }))?.coords ?? "",
                                });
                            }}
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="serviceId">
                            Service:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder={formValues.projectId ? "Search proposal services…" : "Select project first…"}
                            required={false}
                            name="_serviceSelect"
                            inputId="serviceId"
                            fileName="common/searchProjectProposalService.php"
                            value={formValues.serviceId ? { value: formValues.serviceId, label: formValues.serviceName } : null}
                            readOnly={!formValues.projectId}
                            formValues={{ projectId: formValues.projectId }}
                            onChange={next => {
                                setFormValues({
                                    ...formValues,
                                    serviceId: (next as SelectOption)?.value ?? "",
                                    serviceName: (next as SelectOption)?.label ?? "",
                                    leadId: "",
                                });
                                setSelectedUsers([]);
                            }}
                        />
                    </div>

                    {/* All Day + Start + End */}
                    <div className="d-flex align-items-center mt-2">
                        <input
                            id="allDayCheck"
                            type="checkbox"
                            className="cssCheckBox"
                            checked={formValues.allDay === "yes"}
                            onChange={event => setFormValues({
                                ...formValues,
                                allDay: event.currentTarget.checked ? "yes" : "no",
                                startTime: event.currentTarget.checked ? formValues.startTime : "",
                                endTime: event.currentTarget.checked ? formValues.endTime : "",
                            })}
                        />
                        <label className="text-nowrap fw-bold ms-2" htmlFor="allDayCheck">All Day</label>
                        <label className="fw-bold text-nowrap ms-3 required-field" htmlFor="startDate">Start:</label>
                        <input
                            className="form-control flex-grow-1 ms-2"
                            id="startDate"
                            type="date"
                            value={formValues.startDate}
                            onChange={event => setFormValues({ ...formValues, startDate: event.currentTarget.value })}
                            required
                        />
                        {usesTimedSchedule ? (
                            <select
                                className="form-control flex-grow-1 ms-2"
                                id="startTime"
                                value={formValues.startTime}
                                onChange={event => setFormValues({ ...formValues, startTime: event.currentTarget.value })}
                                required
                            >
                                <option value="">Select</option>
                                {timepicker().map(option => (
                                    <option key={option.value} value={option.value}>{option.text}</option>
                                ))}
                            </select>
                        ) : null}
                        <label className="fw-bold text-nowrap ms-3 required-field" htmlFor="endDate">End:</label>
                        <input
                            className="form-control flex-grow-1 ms-2"
                            id="endDate"
                            type="date"
                            value={formValues.endDate}
                            onChange={event => setFormValues({ ...formValues, endDate: event.currentTarget.value })}
                            required
                        />
                        {usesTimedSchedule ? (
                            <select
                                className="form-control flex-grow-1 ms-2"
                                id="endTime"
                                value={formValues.endTime}
                                onChange={event => setFormValues({ ...formValues, endTime: event.currentTarget.value })}
                                required
                            >
                                <option value="">Select</option>
                                {timepicker().map(option => (
                                    <option key={option.value} value={option.value}>{option.text}</option>
                                ))}
                            </select>
                        ) : null}
                    </div>

                    {/* Location */}
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field">Location:</label>
                        <div className="ms-3">
                            <AutoComplete
                                className=""
                                id="location"
                                locationName="location"
                                coordsName="coords"
                                location={formValues.location}
                                coords={toCoords(formValues.coords)}
                                projectLocation={project?.location ?? ""}
                                projectCoords={toCoords(project?.coords)}
                            />
                        </div>
                    </div>

                    {/* Category */}
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="category">Category:</label>
                        <select
                            id="category"
                            name = "category"
                            className="form-control flex-grow-1 ms-3"
                            value={formValues.category}
                            onChange={event => setFormValues({ ...formValues, category: event.currentTarget.value, subCategory: "" })}
                            required
                        >
                            <option value="">Select</option>
                            {Object.keys(projectCategoryColorCode).map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>

                    {/* Sub Category */}
                    {subCategoryOptions.length ? (
                        <div className="d-flex align-items-center mt-2">
                            <label className="fw-bold text-nowrap mb-0" htmlFor="subCategory">Sub Category:</label>
                            <select
                                id="subCategory"
                                name="subCategory"
                                className="form-control flex-grow-1 ms-3"
                                value={formValues.subCategory}
                                onChange={event => setFormValues({ ...formValues, subCategory: event.currentTarget.value })}
                            >
                                <option value="">Select</option>
                                {subCategoryOptions.map((option: string) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                    ) : null}
                    {/* Supervisor */}
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="supervisorId">
                            Supervisor:
                        </label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder="Search supervisor…"
                            required={true}
                            name="supervisorId"
                            inputId="supervisorId"
                            fileName="common/searchUser.php"
                            value={formValues.supervisorId ? { value: formValues.supervisorId, label: formValues.supervisorName } : null}
                            onChange={next =>
                                setFormValues({ ...formValues, supervisorId: (next as SelectOption)?.value ?? "", supervisorName: (next as SelectOption)?.label ?? "" })
                            }
                        />
                    </div>
                    {/* Site Contact */}
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="siteContactId">Site Contact:</label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder="Search site contact…"
                            name="siteContactId"
                            inputId="siteContactId"
                            fileName="common/searchContactInOrganization.php"
                            value={formValues.siteContactId ? { value: formValues.siteContactId, label: formValues.siteContactName } : null}
                            onChange={next =>
                                setFormValues({ ...formValues, siteContactId: (next as SelectOption)?.value ?? "", siteContactName: (next as SelectOption)?.label ?? "" })
                            }
                            formValues = {{ organizationId: String(project.organizationId) ?? "" }}
                        />
                    </div>
     
                    <div className="d-flex align-items-center mt-2">
                        <input
                            id="cadRequired"
                            type="checkbox"
                            className="cssCheckBox"
                            checked={formValues.cadRequired === "yes"}
                            onChange={event => setFormValues({ ...formValues, cadRequired: event.currentTarget.checked ? "yes" : "no" })}
                        />
                        <label className="fw-bold ms-2" htmlFor="cadRequired">CAD Required</label>
                        <input
                            id="reportRequired"
                            type="checkbox"
                            className="cssCheckBox ms-3"
                            checked={formValues.reportRequired === "yes"}
                            onChange={event => setFormValues({ ...formValues, reportRequired: event.currentTarget.checked ? "yes" : "no" })}
                        />
                        <label className="fw-bold ms-2" htmlFor="reportRequired">Report Required</label>
                        <input
                            id="waiveJSA"
                            type="checkbox"
                            className="cssCheckBox ms-3"
                            checked={formValues.waiveJSA === "yes"}
                            onChange={event => setFormValues({ ...formValues, waiveJSA: event.currentTarget.checked ? "yes" : "no" })}
                        />
                        <label className="fw-bold ms-2" htmlFor="jobTagLocation">Waive JSA</label>
                        <input
                            id="jobTagLocation"
                            type="checkbox"
                            className="cssCheckBox ms-3"
                            checked={formValues.jobTagLocation === "yes"}
                            onChange={event => setFormValues({ ...formValues, jobTagLocation: event.currentTarget.checked ? "yes" : "no" })}
                        />
                        <label className="fw-bold ms-2" htmlFor="waiveJSA">Job Tag Location Required</label>
                    </div>

                    {/* Technicians */}
                    <div className="d-flex align-items-center mt-2">
                        <label className="fw-bold text-nowrap required-field" htmlFor="techSelect">Technician(s):</label>
                        <AsyncFormSelect
                            className="flex-grow-1 ms-3"
                            placeholder={
                                !formValues.serviceId
                                    ? "Select service first…"
                                    : !hasScheduleBounds
                                        ? "Enter start and end time first…"
                                        : "Search available technicians…"
                            }
                            required={false}
                            name="_techSelect"
                            inputId="techSelect"
                            fileName="common/searchAvailableTechnician.php"
                            value={selectedTechOptions}
                            isMulti={true}
                            readOnly={!technicianSelectEnabled}
                            formValues={{
                                serviceId: formValues.serviceId,
                                startTime: normalizedStartTime,
                                endTime: normalizedEndTime,
                                workId: String(work.id ?? ""),
                            }}
                            onChange={next => handleTechniciansChange(next as SelectOption[] | null)}
                        />
                    </div>

                    {/* Per-technician detail rows */}
                    {selectedUsers.length ? (
                        <div className="rounded border mt-3">
                            {selectedUsers.map(user => (
                                <div key={user.userId} className="d-flex align-items-center flex-wrap p-2 gap-2">
                                    <strong>{user.userName}</strong>
                                    <select
                                        className="form-control"
                                        style={{ maxWidth: 260 }}
                                        value={user.laborCategory}
                                        onChange={event => updateSelectedUser(user.userId, "laborCategory", event.currentTarget.value)}
                                        required
                                    >
                                        <option value="">Select Labor Category</option>
                                        {laborCategorys.map(option => <option key={option}>{option}</option>)}
                                    </select>
                                    <AsyncFormSelect
                                        className=""
                                        placeholder="Select fleet number…"
                                        name={`_fleetNumber_${user.userId}`}
                                        inputId={`fleetNumber_${user.userId}`}
                                        fileName="common/searchFleet.php"
                                        value={user.fleetNumber ? { value: user.fleetNumber, label: user.fleetName } : null}
                                        onChange={next => {
                                            updateSelectedUser(user.userId, "fleetNumber", (next as SelectOption)?.value ?? "");
                                            updateSelectedUser(user.userId, "fleetName", (next as SelectOption)?.label ?? "");
                                        }}
                                    />
                     
                                     <label className="d-flex align-items-center mb-0">
                                        <input
                                            type="checkbox"
                                            className="cssCheckBox ms-3"
                                            name="_leadId"
                                            value={user.userId}
                                            checked={user.perDiem === "yes"}
                                            onChange={event => updateSelectedUser(user.userId, "perDiem", event.currentTarget.checked ? "yes" : "no")}
                                        />
                                        <span className="ms-2">Per Diem</span>
                                    </label>
                                    <label className="d-flex align-items-center mb-0">
                                        <input
                                            type="radio"
                                            className="cssCheckBox ms-3"
                                            name="_leadId"
                                            value={user.userId}
                                            checked={formValues.leadId === user.userId}
                                            onChange={event => setFormValues({ ...formValues, leadId: event.currentTarget.value })}
                                        />
                                        <span className="ms-2">JSA</span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Description */}
            <div className="px-3">
                <label className="fw-bold mt-2" htmlFor="description">Description:</label>
                <div className="mt-2">
                    <CKEditor
                        id="description"
                        editor={ClassicEditor}
                        config={{
                            toolbar: ["selectAll", "undo", "redo", "bold", "italic", "numberedList", "bulletedList"],
                        }}
                        data={formValues.description}
                        onChange={(_event: unknown, editor: { getData: () => string }) =>
                            setFormValues({ ...formValues, description: editor.getData() })
                        }
                    />
                </div>
                <div className="d-flex mt-3 mb-2">
                    <div className="input-group">
                        <input
                            type="file"
                            multiple
                            className="form-control"
                            name="files[]"
                            id="attachments"
                            onChange={handleFilesInput}
                        />
                    </div>
                </div>
            </div>

            <div className="my-3">
                <button type="submit" className="btn btn-primary">{keyword}</button>
                <Link
                    children="Back"
                    to={".."}
                    className="btn btn-outline-secondary ms-3"
                />
            </div>
        </Form>
    );
}
